import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { PasswordChangeError, PasswordChangeForm, PasswordResetForm, PasswordVerificationDetails } from '@hq-app/login/models/passwords';
import { signIn, signOut, getCurrentUser, fetchAuthSession, updatePassword, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { routeParts } from '@hq-core/routes';
import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
// eslint-disable-next-line no-console
const logAuth = (...args: any[]) => console.log('[AUTH]', ...args);
import { UserCredentials } from '../models/login';
import { DialogService } from '../dialog/dialog.service';
import { DialogConfig, DialogType } from '../models/dialog';
import { SimpleDialogComponent } from '../dialog/simple-dialog/simple-dialog.component';

@Injectable({
    providedIn: 'root'
})
export class AuthService implements OnDestroy {
    private loggedInState = new BehaviorSubject<boolean>(false);
    private sessionExpiredDialogRef: any = null;

    constructor(
        private router: Router,
        private dialogService: DialogService
    ) {
        Hub.listen('auth', evt => this.onAuthEvent(evt));
        this.isAuthenticated();
    }

    ngOnDestroy(): void {
        this.loggedInState.complete();
    }

    isAuthenticated(): Observable<boolean> {
        // Had to catch the rejected promise here to prevent a console error about a rejected promise
        // Related to: https://github.com/angular/angular/issues/31680
        const userAuthenticationPromise = getCurrentUser()
            .catch(() => false);

        return from(userAuthenticationPromise)
            .pipe(
                catchError(() => of(false)),
                map(isAuthorized => !!isAuthorized),
                tap(val => this.loggedInState.next(val))
            );
    }

    refreshSession(): Observable<boolean> {
        logAuth('refreshSession called');
        return from(fetchAuthSession({ forceRefresh: true }))
            .pipe(
                tap(() => logAuth('refreshSession: token refresh succeeded')),
                switchMap(() => this.isAuthenticated()),
                catchError(err => {
                    logAuth('refreshSession failed:', { name: err?.name, message: err?.message });
                    return of(false);
                })
            );
    }

    redirectToLogin(url: string): Observable<boolean> {
        // Don't redirect if already on login page
        if (this.router.url.startsWith('/' + routeParts.login)) {
            return of(true);
        }

        return from(this.router.navigate([routeParts.login], {
            queryParams: { redir: url }
        })).pipe(
            tap(() => {
                this.showSessionExpiredDialog();
            })
        );
    }

    private showSessionExpiredDialog(): void {
        if (this.sessionExpiredDialogRef) {
            return;
        }

        const dialogConfig = new DialogConfig({
            component: SimpleDialogComponent,
            type: DialogType.Simple,
            data: {
                title: 'Session Expired',
                content: 'Your session has expired. Please log in again.',
                actionButtonText: 'Ok'
            },
            returnRef: true
        });

        this.sessionExpiredDialogRef = this.dialogService.show(dialogConfig);
        this.sessionExpiredDialogRef.pipe(take(1)).subscribe(() => {
            this.sessionExpiredDialogRef = null;
        });
    }

    authenticate(userCredentials: UserCredentials): Observable<any> {
        const username = userCredentials.username.toLowerCase().trim();
        const password = userCredentials.password;
        return from(signIn({ username, password })).pipe(
            tap(result => logAuth('signIn result:', result)),
            catchError(err => {
                logAuth('signIn error:', { name: err?.name, code: err?.code, message: err?.message });
                if (err.name === 'UserAlreadyAuthenticatedException') {
                    logAuth('Clearing stale session and retrying signIn');
                    return from(signOut()).pipe(
                        switchMap(() => from(signIn({ username, password })))
                    );
                }
                return throwError(() => err);
            })
        );
    }

    logout(): Observable<any> {
        return from(signOut());
    }

    getAuthToken(): Observable<string> {
        return from(fetchAuthSession())
            .pipe(
                catchError(() => of(null)),
                map(session => session?.tokens?.idToken?.toString())
            );
    }

    /* Returns an Observable of the login state - true means the
       user is logged in */
    getLoginState(): Observable<boolean> {
        return this.loggedInState;
    }

    changePassword(passwordChange: PasswordChangeForm): Observable<string> {
        return from(updatePassword({
            oldPassword: passwordChange.oldPassword,
            newPassword: passwordChange.newPassword
        })).pipe(
            map(() => 'SUCCESS')
        );
    }

    startPasswordReset(username: string): Observable<PasswordVerificationDetails> {
        return from(resetPassword({ username }))
            .pipe(
                map(response => new PasswordVerificationDetails({
                    destination: response?.nextStep?.codeDeliveryDetails?.destination
                }))
            );
    }

    finishPasswordReset(passwordResetForm: PasswordResetForm): Observable<boolean> {
        const request = confirmResetPassword({
            username: passwordResetForm.username,
            confirmationCode: passwordResetForm.verificationCode,
            newPassword: passwordResetForm.password
        });
        return from(request)
            .pipe(
                catchError(error => {
                    return throwError(new PasswordChangeError({
                         
                        name: error.name ?? error.__type
                    }));
                }),
                map(() => true)
            );
    }

    private onAuthEvent(data: any) {
        switch (data.payload.event) {
            case 'signedIn': {
                this.loggedInState.next(true);
                break;
            }
            case 'signedOut':
            case 'tokenRefresh_failure': {
                this.loggedInState.next(false);
                break;
            }
            default: {
                break;
            }
        }
    }
}
